import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Credenza,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza';
import { Button } from '@/components/ui/button.tsx';
import { LoaderIcon, Trash2Icon } from 'lucide-react';
import { useDeleteApiKeyMutation } from '@/features/user-profile/hooks/mutations/use-delete-api-key.ts';
import { useState } from 'react';

type ApiKey = {
  keyId: string;

  name: string;
  partialHint: string;

  createdAt: string;
  lastUsedAt: string;
};

export const ApiKeysTable = ({ apiKeys }: { apiKeys: ApiKey[] }) => {
  if (!apiKeys?.length || apiKeys?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-gray-500">No API keys found</p>
        <p className="mt-1 text-xs text-gray-400">Create your first API key to get started</p>
      </div>
    );
  }
  return (
    <div className="max-h-[400px] overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Key</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Last Used</TableHead>
            <TableHead className="w-[60px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="text-nowrap">
          {apiKeys?.map((apiKey) => (
            <TableRow key={apiKey.keyId}>
              <TableCell className="font-medium">{apiKey.name}</TableCell>
              <TableCell>
                <code className="rounded bg-gray-100 px-2 py-1 font-mono text-xs">
                  {apiKey.partialHint}
                </code>
              </TableCell>
              <TableCell className="text-sm text-gray-600">
                {formatDate(apiKey.createdAt)}
              </TableCell>
              <TableCell className="text-sm text-gray-600">
                {formatLastUsed(apiKey.lastUsedAt)}
              </TableCell>
              <TableCell>
                <DeleteApiKeyDialog apiKey={apiKey} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const DeleteApiKeyDialog = ({ apiKey }: { apiKey: ApiKey }) => {
  const [open, setOpen] = useState(false);
  const { mutate, isPending } = useDeleteApiKeyMutation();

  const handleDelete = () => {
    mutate(
      { id: apiKey.keyId },
      {
        onSuccess: () => {
          setOpen(false);
        },
      }
    );
  };

  return (
    <Credenza open={open} onOpenChange={setOpen}>
      <CredenzaTrigger asChild>
        <Button
          variant="destructive"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label={`Delete API key ${apiKey.name}`}
        >
          <Trash2Icon className="h-4 w-4" />
        </Button>
      </CredenzaTrigger>
      <CredenzaContent>
        <CredenzaHeader>
          <CredenzaTitle>Delete API Key</CredenzaTitle>
          <CredenzaDescription>
            Are you sure you want to delete the API key <b>"{apiKey.name}"</b>pla? This action
            cannot be undone and will immediately revoke access for any applications using this
            key.
          </CredenzaDescription>
        </CredenzaHeader>

        <CredenzaFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={isPending}>
            {isPending && (
              <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
            )}
            {isPending ? 'Deleting...' : 'Delete API Key'}
          </Button>
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
};

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'Invalid date';
  }
}

function formatLastUsed(dateString: string): string {
  if (!dateString) return 'Never';

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;

    return formatDate(dateString);
  } catch {
    return 'Unknown';
  }
}
